import { hashString as murmurHash } from '@w/utils'
import type { AccentColorName } from '../components/avatar'

export function hashString(str: string): number {
  let hash = 0
  for (const char of str) {
    const codePoint = char.codePointAt(0)
    hash = Math.trunc(hash * 31 + (codePoint ?? 0))
  }

  return Math.abs(hash)
}

/** Accents that keep ≥4.5:1 against surface-0 in light and dark (checked from chromatic.css in presence-colour.node.test.ts). */
export const presenceColourNames = [
  'pink',
  'red',
  'orange',
  'yellow',
  'lightgreen',
  'green',
  'lightblue',
  'blue',
  'purple',
  'violet',
  'gray',
] as const satisfies readonly AccentColorName[]

export type PresenceColourName = (typeof presenceColourNames)[number]

/** Colour of `userId` in a room. Every observer passing the same room gets the same answer:
 * userIds are assigned in sorted order, each taking its hashed colour or the next free one.
 * `roomUserIds` is the whole room, the observer included; leaving the observer out can change others' colours.
 * The whole userId is hashed (murmur, @w/utils): ULIDs share their time prefix, which the name hash above drops past ~11 chars.
 */
export function presenceColour(
  userId: string,
  roomUserIds: readonly string[],
): PresenceColourName {
  const size = presenceColourNames.length
  const sorted = [...new Set([...roomUserIds, userId])].toSorted()
  let taken = new Set<number>()
  for (const id of sorted) {
    if (taken.size === size) taken = new Set()
    let index = Number.parseInt(murmurHash(id).slice(-8), 16) % size
    while (taken.has(index)) index = (index + 1) % size
    if (id === userId) return presenceColourNames[index]
    taken.add(index)
  }
  throw new Error('presenceColour: userId missing from its own room')
}

/**
 * CSS colour value for a presence colour, for inline styles (carets, flags). Names the chromatic.css token, never
 * `--color-<name>`: tailwind-config.css is `@theme inline`, so the `--color-*` names exist only inside utilities and
 * a `var(--color-pink)` resolves to nothing at runtime.
 */
export function presenceColourCss(name: PresenceColourName): string {
  return `var(--${name})`
}
